import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type RateSlot = {
    rate: number;
    date: string;
    date_label: string;
};

type RatesResponse = {
    today: RateSlot | null;
    yesterday: RateSlot | null;
    fetched_at: string;
};

const fmtBrl = (rate: number) =>
    rate.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 });

export default function HeaderUsdRates() {
    const { t } = useI18n();
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const { data, isLoading, isError } = useQuery({
        queryKey: ['meta', 'usd-brl'],
        queryFn: async () => (await api.get<RatesResponse>('/meta/usd-brl')).data,
        staleTime: 15 * 60 * 1000,
        refetchInterval: 15 * 60 * 1000,
        retry: 1,
    });

    const boxClass = isDark
        ? 'border-gray-600 bg-gray-800'
        : 'border-gray-200 bg-white shadow-sm';
    const labelClass = isDark ? 'text-gray-400' : 'text-gray-500';
    const dateClass = isDark ? 'text-gray-500' : 'text-gray-400';
    const dividerClass = isDark ? 'bg-gray-600' : 'bg-gray-200';
    const todayValueClass = isDark ? 'text-gray-50' : 'text-gray-900';
    const yesterdayValueClass = isDark ? 'text-gray-300' : 'text-gray-600';

    if (isLoading) {
        return (
            <div
                className={`h-8 w-28 animate-pulse rounded-lg border ${boxClass}`}
                aria-hidden
            />
        );
    }

    if (isError || (!data?.today && !data?.yesterday)) {
        return null;
    }

    return (
        <div
            className={`flex items-center gap-2 rounded-lg border px-2 py-1 text-[10px] sm:px-2.5 sm:text-xs ${boxClass}`}
            title={t('v2.header.usd_rates_title')}
        >
            {data.today ? (
                <div className="leading-tight">
                    <div
                        className={`text-[10px] font-medium uppercase tracking-wide ${labelClass}`}
                    >
                        {t('v2.header.usd_today')}
                    </div>
                    <div className={`font-semibold tabular-nums ${todayValueClass}`}>
                        R$ {fmtBrl(data.today.rate)}
                    </div>
                </div>
            ) : null}
            {data.today && data.yesterday ? (
                <span className={`h-8 w-px shrink-0 ${dividerClass}`} />
            ) : null}
            {data.yesterday ? (
                <div className="leading-tight">
                    <div
                        className={`text-[10px] font-medium uppercase tracking-wide ${labelClass}`}
                    >
                        {t('v2.header.usd_yesterday')}
                        <span className={`ml-0.5 font-normal normal-case ${dateClass}`}>
                            {data.yesterday.date_label}
                        </span>
                    </div>
                    <div className={`font-medium tabular-nums ${yesterdayValueClass}`}>
                        R$ {fmtBrl(data.yesterday.rate)}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
